import React, { useState, useRef, useCallback, useEffect } from "react";
import { TextField, IconButton, Stack, InputAdornment } from "@mui/material";
import { Clear as ClearIcon } from "@mui/icons-material";

interface FileSearchProps {
  onSearch: (searchTerm: string | null) => void;
  placeholder?: string;
  fullWidth?: boolean;
  debounceMs?: number;
  initialValue?: string;
  autoComplete?: string;
  /** When set, renders a floating outlined label that lifts on focus/value. */
  label?: string;
  size?: "small" | "medium";
}

const FileSearch: React.FC<FileSearchProps> = ({
  onSearch,
  placeholder = "Search files and folders...",
  fullWidth = true,
  debounceMs = 300,
  initialValue = "",
  autoComplete = "off",
  label,
  size = "medium",
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue ?? "");
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setSearchTerm(initialValue ?? "");
  }, [initialValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        onSearch(value.trim() || null);
      }, debounceMs);
    },
    [onSearch, debounceMs],
  );

  const handleClear = useCallback(() => {
    setSearchTerm("");
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    onSearch(null);
  }, [onSearch]);

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        fullWidth={fullWidth}
        variant="outlined"
        label={label}
        placeholder={label ? undefined : placeholder}
        value={searchTerm}
        onChange={handleChange}
        size={size}
        inputProps={{ autoComplete }}
        InputProps={{
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton onClick={handleClear} edge="end" sx={{ mr: 0.2 }}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "40px",
            boxSizing: "border-box",
            padding: 0,
          },
          "& .MuiOutlinedInput-input": {
            padding: "0 12px",
            lineHeight: "normal",
            boxSizing: "border-box",
          },
        }}
      />
    </Stack>
  );
};

export default FileSearch;
